import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from 'components/layout';
import HeadData from 'components/head-data';
import MortgageInputs from 'components/mortgage-calculator/MortgageInputs';
import MortgageResults from 'components/mortgage-calculator/MortgageResults';
import AmortizationChart from 'components/mortgage-calculator/AmortizationChart';
import InterestInsightChart from 'components/mortgage-calculator/InterestInsightChart';
import AIInsights from 'components/mortgage-calculator/AIInsights';
import FutureHomePlanner from 'components/mortgage-calculator/FutureHomePlanner';
import ScenarioComparison from 'components/mortgage-calculator/ScenarioComparison';
import SaveShare from 'components/mortgage-calculator/SaveShare';
import EducationalContent from 'components/mortgage-calculator/EducationalContent';
import BorrowingCapacityCalculator from 'components/mortgage-calculator/BorrowingCapacityCalculator';
import LandTransferTaxCalculator from 'components/mortgage-calculator/LandTransferTaxCalculator';
import { calculateMortgage } from 'utils/mortgage-calculator/mortgageCalculations';
import { Card } from 'components/mortgage-calculator/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/mortgage-calculator/ui/tabs';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { NOT_FOUND } from 'constants/not-found';
import { FeaturesType } from 'contexts/features';
import { TenantTypes } from '@tenants';
import { AnalyticsController } from 'analytics/analytics-controller';
import {
  GTM_CALC_RETURN_7D_VIEW,
  GTM_CALC_INPUT_FIRST_EDIT,
  GTM_CALC_INPUT_PRICE_EDIT,
  GTM_CALC_INPUT_DOWN_PCT_EDIT,
  GTM_CALC_INPUT_RATE_EDIT,
  GTM_CALC_FORM_COMPLETE,
  GTM_CALC_RESULT_TIME,
  GTM_CALC_RESULTS_SCROLL_VIEW,
  GTM_CALC_RECALC_TIME,
} from 'constants/events';
import type { GetServerSidePropsContext } from 'next';
import styles from './style.module.scss';

const MORTGAGE_CALC_LAST_VISIT_KEY = 'mortgage_calc_last_visit';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const handler: HandlerType = async (_context: GetServerSidePropsContext, features?: FeaturesType, _tenant?: TenantTypes) => {
  // Check if the mortgage calculator feature is enabled
  if (!features?.useMortgageCalculator) {
    return NOT_FOUND;
  }
  return { props: {} };
};

export const getServerSideProps = createGetServerSideProps(handler);

export default function MortgageCalculatorPage() {
  const [homePrice, setHomePrice] = useState(800000);
  const [downPayment, setDownPayment] = useState(160000);
  const [interestRate, setInterestRate] = useState(5.25);
  const [amortization, setAmortization] = useState(25);
  const [mortgageType, setMortgageType] = useState('fixed');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [propertyTax, setPropertyTax] = useState(4000);
  const [condoFees, setCondoFees] = useState(0);
  const [heatingCosts, setHeatingCosts] = useState(150);

  // --- GTM Tracking Refs ---
  const hasTrackedFirstEdit = useRef(false);
  const hasTrackedFormComplete = useRef(false);
  const hasTrackedResultsScroll = useRef(false);
  const pageLoadTime = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const resultsRef = useRef<HTMLDivElement>(null);
  const chartsRef = useRef<HTMLDivElement>(null);

  // Track 7-day return visitors (UiCalcRet7dView)
  useEffect(() => {
    try {
      const lastVisit = localStorage.getItem(MORTGAGE_CALC_LAST_VISIT_KEY);
      const now = Date.now();
      if (lastVisit) {
        const elapsed = now - Number(lastVisit);
        if (elapsed <= SEVEN_DAYS_MS) {
          AnalyticsController.trackEvent(GTM_CALC_RETURN_7D_VIEW, 'Mortgage Calculator');
        }
      }
      localStorage.setItem(MORTGAGE_CALC_LAST_VISIT_KEY, now.toString());
    } catch {
      // localStorage may not be available
    }
  }, []);

  // Track scroll into view for results and amortization/insights sections
  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Track form completion when results section scrolls into view after user interaction
          if (entry.target === resultsRef.current && !hasTrackedFormComplete.current && hasTrackedFirstEdit.current) {
            hasTrackedFormComplete.current = true;
            const timeToResult = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - pageLoadTime.current;
            AnalyticsController.trackEvent(GTM_CALC_FORM_COMPLETE, 'Mortgage Calculator');
            AnalyticsController.trackEvent(GTM_CALC_RESULT_TIME, 'Mortgage Calculator', undefined, Math.round(timeToResult));
          }
          // Track when amortization/insights charts section scrolls into view
          if (entry.target === chartsRef.current && !hasTrackedResultsScroll.current) {
            hasTrackedResultsScroll.current = true;
            AnalyticsController.trackEvent(GTM_CALC_RESULTS_SCROLL_VIEW, 'Mortgage Calculator');
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, { threshold: 0.3 });
    if (resultsRef.current) observer.observe(resultsRef.current);
    if (chartsRef.current) observer.observe(chartsRef.current);

    return () => observer.disconnect();
  }, []);

  // Helper to track first input interaction (UiCalcInpFirstEdit)
  const trackFirstEdit = useCallback(() => {
    if (!hasTrackedFirstEdit.current) {
      hasTrackedFirstEdit.current = true;
      AnalyticsController.trackEvent(GTM_CALC_INPUT_FIRST_EDIT, 'Mortgage Calculator');
    }
  }, []);

  // Helper to track recalculation timing
  const withRecalcTiming = useCallback((fn: (value: number) => void) => {
    return (value: number) => {
      const start = performance.now();
      fn(value);
      const elapsed = performance.now() - start;
      AnalyticsController.trackEvent(GTM_CALC_RECALC_TIME, 'Mortgage Calculator', undefined, Math.round(elapsed));
    };
  }, []);

  // Tracked input handlers
  const handleHomePriceChange = useCallback((value: number) => {
    trackFirstEdit();
    AnalyticsController.trackEvent(GTM_CALC_INPUT_PRICE_EDIT, 'Mortgage Calculator');
    setHomePrice(value);
  }, [trackFirstEdit]);

  const handleDownPaymentChange = useCallback((value: number) => {
    trackFirstEdit();
    AnalyticsController.trackEvent(GTM_CALC_INPUT_DOWN_PCT_EDIT, 'Mortgage Calculator');
    setDownPayment(value);
  }, [trackFirstEdit]);

  const handleInterestRateChange = useCallback((value: number) => {
    trackFirstEdit();
    AnalyticsController.trackEvent(GTM_CALC_INPUT_RATE_EDIT, 'Mortgage Calculator');
    setInterestRate(value);
  }, [trackFirstEdit]);

  const handleAmortizationChange = useCallback((value: number) => {
    trackFirstEdit();
    withRecalcTiming(setAmortization)(value);
  }, [trackFirstEdit, withRecalcTiming]);

  const handleMortgageTypeChange = useCallback((value: string) => {
    trackFirstEdit();
    setMortgageType(value);
  }, [trackFirstEdit]);

  const handlePaymentFrequencyChange = useCallback((value: string) => {
    trackFirstEdit();
    setPaymentFrequency(value);
  }, [trackFirstEdit]);

  const handlePropertyTaxChange = useCallback((value: number) => {
    trackFirstEdit();
    setPropertyTax(value);
  }, [trackFirstEdit]);

  const handleCondoFeesChange = useCallback((value: number) => {
    trackFirstEdit();
    setCondoFees(value);
  }, [trackFirstEdit]);

  const handleHeatingCostsChange = useCallback((value: number) => {
    trackFirstEdit();
    setHeatingCosts(value);
  }, [trackFirstEdit]);

  const results = calculateMortgage(
    homePrice,
    downPayment,
    interestRate,
    amortization,
    propertyTax,
    condoFees,
    heatingCosts,
    false
  );

  return (
    <Layout>
      <HeadData data={{ title: 'Mortgage Calculator - Calculate Your Mortgage Payments' }} />
      <div className={styles.page}>
        <main className={styles.main}>
          <Tabs defaultValue="calculator" className={styles.tabsContainer}>
            {/* Navigation Tabs */}
            <TabsList>
              <TabsTrigger value="planner">
                Future Planner
              </TabsTrigger>
              <TabsTrigger value="calculator">
                Mortgage Calculator
              </TabsTrigger>
              <TabsTrigger value="borrowing">
                Borrowing & Tax
              </TabsTrigger>
              <TabsTrigger value="compare">
                Compare
              </TabsTrigger>
              <TabsTrigger value="learn">
                Learn
              </TabsTrigger>
            </TabsList>

            {/* Mortgage Calculator Tab */}
            <TabsContent value="calculator" className={styles.tabContent}>
              <div className={styles.pageHeader}>
                <h2>
                  Mortgage Payment Calculator
                </h2>
                <p>
                  Calculate your mortgage payments with accurate Canadian formulas
                </p>
              </div>

              {/* Save/Share Actions */}
              <div className={styles.saveShareContainer}>
                <SaveShare
                  mortgageData={{
                    homePrice,
                    downPayment,
                    interestRate,
                    amortization,
                    monthlyPayment: results.monthlyPayment,
                    totalInterest: results.totalInterest,
                  }}
                />
              </div>

              {/* Main Calculator Grid */}
              <div className={styles.calculatorGrid}>
                {/* Left Column - Inputs */}
                <div className={styles.column}>
                  <Card className={styles.inputCard}>
                    <h2 className={styles.inputCardTitle}>Mortgage Details</h2>
                    <MortgageInputs
                      homePrice={homePrice}
                      downPayment={downPayment}
                      interestRate={interestRate}
                      amortization={amortization}
                      mortgageType={mortgageType}
                      paymentFrequency={paymentFrequency}
                      propertyTax={propertyTax}
                      condoFees={condoFees}
                      heatingCosts={heatingCosts}
                      onHomePriceChange={handleHomePriceChange}
                      onDownPaymentChange={handleDownPaymentChange}
                      onInterestRateChange={handleInterestRateChange}
                      onAmortizationChange={handleAmortizationChange}
                      onMortgageTypeChange={handleMortgageTypeChange}
                      onPaymentFrequencyChange={handlePaymentFrequencyChange}
                      onPropertyTaxChange={handlePropertyTaxChange}
                      onCondoFeesChange={handleCondoFeesChange}
                      onHeatingCostsChange={handleHeatingCostsChange}
                    />
                  </Card>
                </div>

                {/* Middle Column - Results */}
                <div className={styles.column} ref={resultsRef}>
                  <MortgageResults results={results} paymentFrequency={paymentFrequency} />
                </div>

                {/* Right Column - AI Insights */}
                <div className={styles.column}>
                  <AIInsights
                    homePrice={homePrice}
                    downPayment={downPayment}
                    interestRate={interestRate}
                    amortization={amortization}
                    monthlyPayment={results.monthlyPayment}
                    totalInterest={results.totalInterest}
                    cmhcInsurance={results.cmhcInsurance}
                  />
                </div>
              </div>

              {/* Chart Section */}
              <div ref={chartsRef}>
                <AmortizationChart
                  principal={results.principalAmount}
                  interestRate={interestRate}
                  amortization={amortization}
                />

                {/* Interest Insight Chart */}
                <InterestInsightChart
                  principal={results.principalAmount}
                  interestRate={interestRate}
                  amortization={amortization}
                />
              </div>
            </TabsContent>

            {/* Future Home Planner Tab */}
            <TabsContent value="planner" className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <h2>
                  Future Home Affordability Planner
                </h2>
                <p>
                  Plan your path to homeownership with savings projections
                </p>
              </div>
              <FutureHomePlanner />
            </TabsContent>

            {/* Borrowing Capacity & LTT Tab */}
            <TabsContent value="borrowing" className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <h2>
                  Borrowing Capacity & Land Transfer Tax
                </h2>
                <p>
                  Calculate your borrowing power and closing costs
                </p>
              </div>
              <div className={styles.borrowingGrid}>
                <BorrowingCapacityCalculator />
                <LandTransferTaxCalculator />
              </div>
            </TabsContent>

            {/* Scenario Comparison Tab */}
            <TabsContent value="compare" className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <h2>
                  Scenario Comparison
                </h2>
                <p>
                  Compare different mortgage options side by side
                </p>
              </div>
              <ScenarioComparison />
            </TabsContent>

            {/* Educational Content Tab */}
            <TabsContent value="learn" className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <h2>
                  Mortgage Education Center
                </h2>
                <p>
                  Learn everything you need to know about Canadian mortgages
                </p>
              </div>
              <EducationalContent />
            </TabsContent>
          </Tabs>

          {/* Footer Info */}
          <Card className={styles.footerCard}>
            <p>
              All calculations use accurate Canadian mortgage formulas including CMHC insurance, land transfer tax, and stress test requirements.
              <br />
              This calculator is for informational purposes only. Please consult with a mortgage professional for personalized advice.
            </p>
          </Card>
        </main>
      </div>
    </Layout>
  );
}
